import { Component, ChangeDetectionStrategy, signal, computed, inject, model, output, effect, untracked, resource, linkedSignal } from '@angular/core';
import { environment } from 'src/environments/environment';
import { Consultant, Culture, CustomerMasterData } from '../customer.model';
import { CustomerService } from '../customer.service';
import { MainStore } from 'src/app/common/main.store';
import { ModalService } from 'src/app/services/modal.service';
import { CommonService } from 'src/app/services/common.service';
import { form, FormField, required, email, schema, apply, applyWhen, validate, validateTree, readonly, submit } from '@angular/forms/signals';
import { NgbTooltip } from '@ng-bootstrap/ng-bootstrap';
import { ToastrService } from 'ngx-toastr';
import { ErrorService } from '../../../common/errors/error.service';

// Form model — types match what UI controls expect (string-based)
interface AzureStorageFormModel {
  containerName: string;
  connectionString: string;
}

interface CustomerMasterdataFormModel {
  customerName: string;
  name: string;
  email: string;
  productionDate: string;   // YYYY-MM-DD for date input
  numberOfLicenses: string; // text input => string
  evLicense: string; // Guid as string for select input
  customerResponsibleId: string;
  productionResponsibleId: string;
  implementationResponsibleId: string;
  cultureCode: string;
  cultureCodes: string;
  storage: AzureStorageFormModel;
  personalStorage: AzureStorageFormModel;
}

const storageSchema = schema<AzureStorageFormModel>((p) => {
  required(p.containerName, { message: 'Container name er påkrævet' });
  required(p.connectionString, { message: 'Connectionstring er påkrævet' });
});

const guidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

@Component({
  selector: 'app-customer-masterdata',
  templateUrl: './customer-masterdata.component.html',
  styleUrl: './customer-masterdata.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [FormField, NgbTooltip],
})
export class CustomerMasterdataComponent {
  private readonly modalService = inject(ModalService);
  private readonly customerService = inject(CustomerService);
  private readonly commonService = inject(CommonService);
  private readonly store = inject(MainStore);
  private readonly toastrService = inject(ToastrService);
  private readonly errorService = inject(ErrorService);

  // Domain model input
  readonly customer = model<CustomerMasterData>({} as CustomerMasterData);
  readonly errorMessage = model<{ message: string, location: 'storage' | 'personalStorage' | 'form' } | null>(null);

  readonly formSubmitted = output<CustomerMasterData>();
  readonly isValidChanged = output<boolean>();

  readonly hasStorageError = computed(() => this.errorMessage()?.location === 'storage');
  readonly hasPersonalStorageError = computed(() => this.errorMessage()?.location === 'personalStorage');

  // --- Async data resources ---

  protected readonly consultantsResource = resource({
    params: () => {
      const deptId = this.customer().departmentId ?? this.store.selectedDepartment.get()?.id;
      return deptId ? { departmentId: deptId } : undefined;
    },
    loader: async ({ params }) => {
      const list = await this.customerService.getConsultants(params.departmentId);
      return [{ id: '', name: 'Vælg konsulent', isBoss: false } as Consultant, ...list];
    },
  });

  private readonly culturesResource = resource({
    loader: () => this.customerService.getCultures(),
  });

  protected readonly implementationConsultantsResource = resource({
    params: () => {
      if (!this.consultantsResource.hasValue()) return undefined;
      return { consultants: this.consultantsResource.value(), implementationId: this.customer().implementationResponsibleId };
    },
    loader: async ({ params }) => {
      const { consultants, implementationId } = params;
      const emptyId = this.commonService.guidEmpty();
      if (!implementationId || implementationId === '' || implementationId === emptyId) return consultants;
      if (consultants.find(c => c.id === implementationId)) return consultants;
      const employee = await this.customerService.getEmployee(implementationId);
      if (employee?.id === implementationId && employee.id !== emptyId) {
        return [...consultants, { id: employee.id, name: employee.name, email: employee.email, isBoss: false } as Consultant];
      }
      return consultants;
    },
  });

  // Derived signal: cultures list with selected state driven by formModel.cultureCodes
  // Using computed (not linkedSignal) so checkbox changes via formModel.update() don't trigger a full reset.
  readonly cultures = computed<Culture[]>(() => {
    if (!this.culturesResource.hasValue()) return [];
    const raw = this.culturesResource.value();
    const selected = new Set(this.formModel().cultureCodes.split(',').map(s => s.trim()).filter(Boolean));
    return raw.map(c => ({ ...c, selected: selected.has(c.key) }));
  });

  readonly showStorageConnectionString = linkedSignal(() => !this.customer().storage?.connectionString);
  readonly showPersonalStorageConnectionString = linkedSignal(() => !this.customer().personalStorage?.connectionString);
  readonly testingStorageConnection = signal(false);

  readonly storageOpen = signal(false);
  readonly personalStorageOpen = signal(false);

  readonly isDevEnv = signal(environment.environment === 'Development' || environment.environment === 'Test' || environment.environment === 'Sandbox');
  readonly skipAzureValidation = signal(false);
  readonly AzureValidationRequired = computed(() => !(this.skipAzureValidation() || this.customer().customerCanSkipStorageRequirement));
  readonly evLicenseRequired = computed(() => environment.environment === 'Production' && !this.store.currentUser.get().isAdmin);
  readonly isEvLicenseReadOnly = computed(() => environment.environment === 'Production' && (this.customer().evLicense != null && this.customer().evLicense !== this.commonService.guidEmpty())); 

  // Form model — derived from domain model input via linkedSignal (Angular recommended pattern).
  // Automatically resets to match the domain model when customer changes; writable for in-form edits.
  readonly formModel = linkedSignal({
    source: this.customer,
    computation: (data): CustomerMasterdataFormModel => {
      const cultureCode = data.cultureCode ?? data.cultureCodes?.split(',')[0] ?? 'da-DK';
      // The standard language must always be among the available languages, otherwise the customer's
      // users cannot pick a language at login. Seed the standard into the available set up front.
      const available = new Set((data.cultureCodes ?? '').split(',').map(s => s.trim()).filter(Boolean));
      available.add(cultureCode);
      return {
        customerName: data.customerName ?? data.name ?? '',
        name: data.name ?? '',
        email: data.email ?? '',
        productionDate: data.productionDate ? data.productionDate.substring(0, 10) : '',
        numberOfLicenses: String(data.numberOfLicenses ?? 0),
        evLicense: data.evLicense ?? this.commonService.guidEmpty(),
        customerResponsibleId: data.customerResponsibleId ?? '',
        productionResponsibleId: data.productionResponsibleId ?? '',
        implementationResponsibleId: data.implementationResponsibleId ?? '',
        cultureCode,
        cultureCodes: [...available].join(','),
        storage: { containerName: data.storage?.containerName ?? '', connectionString: data.storage?.connectionString ?? '' },
        personalStorage: { containerName: '', connectionString: data.personalStorage?.connectionString ?? '' },
      };
    },
  });

  readonly customerForm = form(this.formModel, (p) => {
    required(p.customerName, { message: 'Kundenavn er påkrævet' });
    required(p.name, { message: 'Vist navn er påkrævet' });
    required(p.email, { message: 'Email er påkrævet' });
    email(p.email, { message: 'Email er ikke gyldig' });
    required(p.productionDate, { message: 'Dato for idriftsættelse er påkrævet' });
    required(p.customerResponsibleId, { message: 'Kundeansvarlig er påkrævet' });
    required(p.productionResponsibleId, { message: 'Driftansvarlig er påkrævet' });
    required(p.implementationResponsibleId, { message: 'Implementeringsansvarlig er påkrævet' });

    readonly(p.evLicense, () => this.isEvLicenseReadOnly());
    readonly(p.cultureCode, () => this.culturesResource.isLoading());
    readonly(p.customerResponsibleId, () => this.consultantsResource.isLoading());
    readonly(p.productionResponsibleId, () => this.consultantsResource.isLoading());
    readonly(p.implementationResponsibleId, () => this.implementationConsultantsResource.isLoading());

    validate(p.evLicense, ({ value }) => {

      const evLicense = value().trim();
      if (!evLicense || (evLicense === this.commonService.guidEmpty() && this.evLicenseRequired())) {
        return [{ kind: 'required', message: 'EV Licens er påkrævet' }];
      }

      if (!guidRegex.test(evLicense)) {
        return [{ kind: 'invalidGuid', message: 'EV Licens skal være en gyldig GUID' }];
      }

      return null;
    });

    // Apply required storage validators only when Azure validation is required
    applyWhen(p.storage, () => this.AzureValidationRequired(), storageSchema);

    // When not required: if any field has a value, both must be filled
    validateTree(p.storage, ({ value }) => {
      if (this.AzureValidationRequired()) return null;
      const s = value();
      const anyFilled = s.containerName?.trim() || s.connectionString?.trim();
      if (!anyFilled) return null;
      const errors = [];
      if (!s.containerName?.trim())
        errors.push({ kind: 'storageConfigIncomplete', message: 'Begge felter skal udfyldes hvis et af dem er udfyldt' });
      if (!s.connectionString?.trim())
        errors.push({ kind: 'storageConfigIncomplete', message: 'Begge felter skal udfyldes hvis et af dem er udfyldt' });
      return errors.length ? errors : null;
    });

    // Duplicate connection string check
    validate(p.personalStorage.connectionString, ({ value }) => {
      const personal = value();
      const main = this.formModel().storage.connectionString;
      if (personal?.trim() && main?.trim() && personal === main) {
        return [{ kind: 'duplicateConnectionString', message: 'Generel og private connectionstrings skal være forskellige' }];
      }
      return null;
    });
  });

  readonly hasAttemptedSave = signal(false);

  constructor() {
    effect(() => {
      // Surface resource load errors to the user
      if (this.consultantsResource.error() || this.culturesResource.error() || this.implementationConsultantsResource.error()) {
        // HTTP-fejl vises globalt af GlobalErrorInterceptor/ErrorService
      }
    });

    effect(() => {
      // Allow first save attempt regardless of validity.
      // After the first failed attempt, track real validity so the user
      // can see when they've corrected all errors.
      if (!this.hasAttemptedSave()) {
        this.isValidChanged.emit(true);
      } else {
        this.isValidChanged.emit(!this.customerForm().invalid());
      }
    });

    effect(() => {
      // Re-enable save button when user makes any change after a failed attempt.
      this.formModel();
      untracked(() => {
        if (this.hasAttemptedSave()) {
          this.hasAttemptedSave.set(false);
        }
      });
    });
  }

  onToggleSkipAzure(checked: boolean) {
    this.skipAzureValidation.set(checked);
  }

  async showEmployeesModal() {
    const result = await this.modalService.showImplementationManagerModal(event, this.implementationConsultantsResource.hasValue() ? this.implementationConsultantsResource.value() : [], this.customer());
    if (result != null)
      this.formModel.update(m => ({ ...m, implementationResponsibleId: result }));
  }

  onCultureChange(event: Event) {
    const selectedValue = (event.target as HTMLSelectElement).value;
    this.formModel.update(m => {
      const current = new Set(m.cultureCodes.split(',').map(s => s.trim()).filter(Boolean));
      current.add(selectedValue);
      return { ...m, cultureCode: selectedValue, cultureCodes: [...current].join(',') };
    });
  }

  onCultureCheckboxChange(culture: Culture, event: Event) {
    const checked = (event.target as HTMLInputElement).checked;
    this.formModel.update(m => {
      // Never allow removing the standard language from the available set — it must stay selectable at login.
      if (!checked && culture.key === m.cultureCode) return m;
      const current = new Set(m.cultureCodes.split(',').map(s => s.trim()).filter(Boolean));
      if (checked) current.add(culture.key);
      else current.delete(culture.key);
      return { ...m, cultureCodes: [...current].join(',') };
    });
  }

  testStorageConnection(connectionstring: string) {
    this.errorMessage.set(null);
    this.testingStorageConnection.set(true);
    this.customerService.validateAzureStorageConnection(connectionstring)
      .then(valid => {
        if (valid) { this.toastrService.success("Connectionstreng er valid."); }
        else { this.errorService.notify("Connectionstreng er ikke valid."); }
      })
      .catch(() => { /* HTTP-fejl vises globalt af GlobalErrorInterceptor/ErrorService */ })
      .finally(() => { this.testingStorageConnection.set(false); });
  }

  checkStorageContainer(connectionstring: string, containerName: string) {
    this.customerService.checkContainerExists(connectionstring, containerName)
      .then(exists => {
        if (exists) {
          this.toastrService.success("Container findes!");
        } else {
          confirm("Container findes ikke! Opret den?") &&
            this.customerService.createAzureContainer(connectionstring, containerName)
              .then(created => {
                if (created) { this.toastrService.success("Container blev oprettet!"); }
                else { this.errorService.notify("Container blev ikke oprettet."); }
              });
        }
      })
      .finally(() => { this.testingStorageConnection.set(false); });
  }

  toggleStorage(){ this.storageOpen.set(!this.storageOpen()); }
  togglePersonalStorage() { this.personalStorageOpen.set(!this.personalStorageOpen()); }
  toggleShowStorageConnectionString() { this.showStorageConnectionString.set(!this.showStorageConnectionString()); }
  toggleShowPersonalStorageConnectionString() { this.showPersonalStorageConnectionString.set(!this.showPersonalStorageConnectionString()); }

  async submitForm(): Promise<void> {
    this.hasAttemptedSave.set(true);

    await submit(this.customerForm, async () => {
      const f = this.formModel();

      const result: CustomerMasterData = {
        ...this.customer(),
        departmentId: this.customer().departmentId ?? this.store.selectedDepartment.get()!.id,
        customerName: f.customerName,
        name: f.name,
        email: f.email,
        productionDate: f.productionDate?.trim() ? f.productionDate.substring(0, 10) : null,
        numberOfLicenses: Number(f.numberOfLicenses) || 0,
        evLicense: f.evLicense?.trim() ? f.evLicense : this.commonService.guidEmpty(),
        customerResponsibleId: f.customerResponsibleId,
        productionResponsibleId: f.productionResponsibleId,
        implementationResponsibleId: f.implementationResponsibleId,
        cultureCode: f.cultureCode,
        cultureCodes: f.cultureCodes,
        storage: { containerName: f.storage.containerName, connectionString: f.storage.connectionString },
        personalStorage: { containerName: '', connectionString: f.personalStorage.connectionString },
        createdBy: this.customer().createdBy || this.store.currentUser.get().name + ' [' + this.store.currentUser.get().userName + ']',
      };

      this.customer.set(result);
      this.errorMessage.set(null);
      this.formSubmitted.emit(result);
      return null;
    });

    // Open accordions if their fields have errors after the submit attempt
    if (this.customerForm.storage().invalid()) this.storageOpen.set(true);
    if (this.customerForm.personalStorage().invalid()) this.personalStorageOpen.set(true);
  }
}
